import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, FormControl, FormGroup, Validators } from '@angular/forms';
import { ToastService } from '../../ui';
import { LoggingService } from '../../core/services/logging.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmDialogComponent } from '../../shared/confirm-dialog/confirm-dialog.component';
import {
  Reachability,
  ReachabilityType,
  ReachabilityGroup,
  REACHABILITY_GROUPS,
  EXTERNAL_TYPES,
  EMAIL_FORMAT_OPTIONS,
  isSystemGeneratedBusy,
  isSystemAbForwarding,
} from './reachabilities.model';
import { ReachabilitiesService } from './reachabilities.service';
import { ErrorStateComponent } from '../../shared/error-state/error-state.component';
import { extractErrorMessage } from '../../shared/utils/extract-error-message';
import { copyToClipboard } from '../../shared/utils/clipboard';
import { typeBadgeVariant } from '../../shared/utils/reachability-type-badge';
import { CappedListComponent } from '../../shared/capped-list/capped-list.component';
import { RevealOnExpandDirective } from '../../shared/directives/reveal-on-expand.directive';
import { SNACKBAR_ERROR_CONFIG } from '../../shared/utils/notification-defaults';
import {
  HlmBadgeComponent,
  HlmBtnDirective,
  HlmIconComponent,
  HlmInputDirective,
  HlmSelectComponent,
  HlmSwitchComponent,
  HlmSpinnerComponent,
  HLM_FORM_FIELD,
  HlmSelectOption,
} from '../../ui';

@Component({
  selector: 'cmt-reachabilities',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ErrorStateComponent,
    HlmIconComponent,
    HlmBadgeComponent,
    HlmBtnDirective,
    HlmInputDirective,
    HlmSelectComponent,
    HlmSwitchComponent,
    HlmSpinnerComponent,
    CappedListComponent,
    RevealOnExpandDirective,
    ...HLM_FORM_FIELD,
  ],
  templateUrl: './reachabilities.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReachabilitiesComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(ReachabilitiesService);
  private readonly toast = inject(ToastService);
  private readonly loggingService = inject(LoggingService);
  private readonly dialog = inject(MatDialog);
  private readonly destroyRef = inject(DestroyRef);

  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly reachabilities = signal<Reachability[]>([]);

  /** Currently editing row id (null = none) */
  readonly editingId = signal<number | null>(null);
  /** Group key where we are adding a new row */
  readonly addingInGroup = signal<string | null>(null);
  readonly saving = signal(false);

  readonly emailFormatOptions = EMAIL_FORMAT_OPTIONS;

  /** HlmSelect options for email format */
  readonly emailFormatSelectOptions: HlmSelectOption<string>[] =
    EMAIL_FORMAT_OPTIONS.map(o => ({ value: o.value, label: o.label }));

  // Filter
  readonly filterText = signal('');

  // Collapsible state
  readonly collapsed = signal<Record<string, boolean>>({});

  readonly allCollapsed = computed(() => {
    const groups = this.groups();
    return groups.length > 0 && groups.every(g => this.collapsed()[g.key] ?? this.defaultCollapsed(g.key));
  });

  /** Pre-computed filtered items per group key — avoids re-filtering on every CD cycle */
  readonly filteredItemsMap = computed<Map<string, Reachability[]>>(() => {
    const groups = this.groups();
    const q = this.filterText().toLowerCase();
    const map = new Map<string, Reachability[]>();
    for (const g of groups) {
      if (!q) {
        map.set(g.key, g.items);
      } else {
        map.set(g.key, g.items.filter(r =>
          r.name.toLowerCase().includes(q)
          || r.destination.toLowerCase().includes(q)
          || r.type.toLowerCase().includes(q),
        ));
      }
    }
    return map;
  });

  readonly hiddenGroupCount = computed(() => {
    if (!this.filterText()) return 0;
    return this.groups().filter(g => (this.filteredItemsMap().get(g.key)?.length ?? 0) === 0).length;
  });

  private phoneServiceId = 0;

  /**
   * Grouped reachabilities for UI rendering. Items are sorted by their
   * immutable `id`, descending, so an edit never moves a row: the backend
   * returns the list in unstable storage order and re-slots a touched entry
   * after a PUT, which made the edited row jump within its group (EB-4312).
   * Descending (newest first) keeps a just-created reachability visible at the
   * top of its capped group instead of buried last.
   */
  readonly groups = computed<ReachabilityGroup[]>(() => {
    const all = this.reachabilities();
    return REACHABILITY_GROUPS.map(g => ({
      ...g,
      items: all.filter(r => g.types.includes(r.type)).sort((a, b) => b.id - a.id),
    }));
  });

  // --- Inline edit form ---
  readonly editForm = new FormGroup({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    destination: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    publiclyAvailable: new FormControl(false, { nonNullable: true }),
    callNotificationEmailFormat: new FormControl<string | null>(null),
    type: new FormControl<ReachabilityType>('EMAIL', { nonNullable: true }),
  });

  ngOnInit(): void {
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      const id = Number(params['phoneServiceId']);
      if (id) {
        this.phoneServiceId = id;
        this.load();
      }
    });
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.getAll(this.phoneServiceId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.reachabilities.set(data);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(extractErrorMessage(err, 'Failed to load reachabilities'));
        this.loading.set(false);
      },
    });
  }

  // --- Clipboard ---

  copyToClipboard(text: string, event?: Event): void {
    copyToClipboard(this.toast, text, event);
  }

  // --- Helpers ---

  isExternal(r: Reachability): boolean {
    return EXTERNAL_TYPES.includes(r.type);
  }

  isSystemBusy(r: Reachability): boolean {
    return isSystemGeneratedBusy(r);
  }

  canEdit(r: Reachability): boolean {
    return !this.isExternal(r) && !this.isSystemBusy(r) && !isSystemAbForwarding(r);
  }

  canDelete(r: Reachability): boolean {
    return !this.isSystemBusy(r) && !isSystemAbForwarding(r);
  }

  /**
   * Direct + Busy forwardings have no public/private semantic — the column
   * is omitted entirely (not just the switch hidden, which would leave a
   * gap in the grid).
   */
  hasPublicColumn(group: ReachabilityGroup): boolean {
    return group.key !== 'direct' && group.key !== 'busy';
  }

  /**
   * CSS Grid template per group — column count varies by group:
   *   - Email:        Name | Destination | Type | Format | Public | (Actions)
   *   - Direct/Busy:  Name | Destination | Type |        |        | (Actions)
   *   - Other:        Name | Destination | Type |        | Public | (Actions)
   * Actions slot is only added when the group is editable.
   */
  gridColumns(group: ReachabilityGroup, alwaysIncludeActions = false): string {
    const actions = alwaysIncludeActions || !group.readOnly ? ' 210px' : '';
    const type = this.showTypeColumn(group) ? ' 130px' : '';
    if (this.isEmailGroup(group)) return 'minmax(120px,1fr) minmax(140px,1.5fr)' + type + ' 180px 60px' + actions;
    if (!this.hasPublicColumn(group)) return 'minmax(120px,1fr) minmax(140px,1.5fr)' + type + actions;
    return 'minmax(120px,1fr) minmax(140px,1.5fr)' + type + ' 60px' + actions;
  }

  /** Type column is only meaningful when a group can hold more than one type. */
  showTypeColumn(group: ReachabilityGroup): boolean {
    return group.types.length > 1;
  }

  /** Light color scheme for Type badges — shared across reachability views. */
  readonly typeBadgeVariant = typeBadgeVariant;

  isEmailGroup(group: ReachabilityGroup): boolean {
    return group.key === 'email';
  }

  /**
   * Human-readable label for an email-format enum, so the read row matches the
   * edit select's vocabulary (e.g. `HTML` → "HTML + Text") instead of showing
   * the raw enum. Falls back to the raw value for anything not in the map.
   */
  formatLabel(value: string | null): string {
    if (!value) return '';
    return EMAIL_FORMAT_OPTIONS.find(o => o.value === value)?.label ?? value;
  }

  /** Whether a group allows switching between its sub-types (e.g. FAX/SMS) */
  hasSubTypes(group: ReachabilityGroup): boolean {
    return group.types.length > 1 && !group.readOnly;
  }

  /** Default type when adding to a group */
  defaultTypeForGroup(group: ReachabilityGroup): ReachabilityType {
    return group.types[0];
  }

  /** Build HlmSelectOption[] for a group's sub-types */
  typeOptionsForGroup(group: ReachabilityGroup): HlmSelectOption<ReachabilityType>[] {
    return group.types.map(t => ({ value: t, label: t }));
  }

  // --- CRUD ---

  startEdit(r: Reachability): void {
    if (this.saving()) return;
    this.addingInGroup.set(null);
    this.editingId.set(r.id);
    this.editForm.patchValue({
      name: r.name,
      destination: r.destination,
      publiclyAvailable: r.publiclyAvailable,
      callNotificationEmailFormat: r.callNotificationEmailFormat,
      type: r.type,
    });
  }

  cancelEdit(): void {
    this.editingId.set(null);
    this.addingInGroup.set(null);
  }

  saveEdit(): void {
    if (this.editForm.invalid || this.saving()) return;
    const id = this.editingId();
    if (id === null) return;

    this.saving.set(true);
    const val = this.editForm.getRawValue();
    const payload: Partial<Reachability> = {
      name: val.name,
      destination: val.destination,
      publiclyAvailable: val.publiclyAvailable,
      type: val.type,
      callNotificationEmailFormat: val.type === 'EMAIL' ? val.callNotificationEmailFormat : null,
    };

    this.api.update(this.phoneServiceId, id, payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        this.editingId.set(null);
        this.saving.set(false);
        this.load();
        this.toast.open('Reachability updated', 'OK', { duration: 3000 });
      },
      error: (err) => {
        this.saving.set(false);
        this.toast.open(extractErrorMessage(err, 'Failed to update reachability'), 'OK', SNACKBAR_ERROR_CONFIG);
      },
    });
  }

  startAdd(group: ReachabilityGroup): void {
    if (this.saving()) return;
    this.editingId.set(null);
    this.addingInGroup.set(group.key);
    this.editForm.reset({
      name: '',
      destination: '',
      publiclyAvailable: false,
      callNotificationEmailFormat: group.key === 'email' ? 'HTML' : null,
      type: this.defaultTypeForGroup(group),
    });
  }

  saveNew(): void {
    if (this.editForm.invalid || this.saving()) return;

    this.saving.set(true);
    const val = this.editForm.getRawValue();
    const payload: Partial<Reachability> = {
      name: val.name,
      destination: val.destination,
      publiclyAvailable: val.publiclyAvailable,
      type: val.type,
      callNotificationEmailFormat: val.type === 'EMAIL' ? val.callNotificationEmailFormat : null,
    };

    this.api.create(this.phoneServiceId, payload).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: () => {
        /** @ticket EB-3798 — usage: a reachability was successfully created (D9). */
        this.loggingService.usage('action.invoke', { action: 'reachability_create', outcome: 'success' });
        this.addingInGroup.set(null);
        this.saving.set(false);
        this.load();
        this.toast.open('Reachability created', 'OK', { duration: 3000 });
      },
      error: (err) => {
        this.saving.set(false);
        this.toast.open(extractErrorMessage(err, 'Failed to create reachability'), 'OK', SNACKBAR_ERROR_CONFIG);
      },
    });
  }

  confirmDelete(r: Reachability): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Delete Reachability',
        message: `If you delete reachability "${r.name}", all notifications and forwardings referring to it will be automatically deleted as well. Are you sure?`,
        confirmLabel: 'Delete',
        confirmColor: 'warn',
        icon: 'warning',
      },
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;
      this.api.delete(this.phoneServiceId, r.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          this.reachabilities.update(list => list.filter(x => x.id !== r.id));
          this.toast.open(`Reachability "${r.name}" deleted`, 'OK', { duration: 3000 });
        },
        error: (err) => {
          this.toast.open(extractErrorMessage(err, 'Failed to delete reachability'), 'OK', SNACKBAR_ERROR_CONFIG);
        },
      });
    });
  }

  // ========== Collapse / Filter ==========

  /** Groups start expanded — except the read-only External group. */
  private defaultCollapsed(key: string): boolean {
    return key === 'external';
  }

  toggleSection(key: string): void {
    this.collapsed.update(c => ({ ...c, [key]: !(c[key] ?? this.defaultCollapsed(key)) }));
  }

  isCollapsed(key: string): boolean {
    return this.collapsed()[key] ?? this.defaultCollapsed(key);
  }

  toggleAll(): void {
    const shouldCollapse = !this.allCollapsed();
    const next: Record<string, boolean> = {};
    for (const g of this.groups()) next[g.key] = shouldCollapse;
    this.collapsed.set(next);
  }

  /** Filter items within a group by search text */
  filteredItems(group: ReachabilityGroup): Reachability[] {
    const q = this.filterText().toLowerCase();
    if (!q) return group.items;
    return group.items.filter(r =>
      r.name.toLowerCase().includes(q)
      || r.destination.toLowerCase().includes(q)
      || r.type.toLowerCase().includes(q),
    );
  }

  /** Whether a group has any matching results (used to hide empty groups during filtering) */
  groupHasResults(group: ReachabilityGroup): boolean {
    if (!this.filterText()) return true;
    return this.filteredItems(group).length > 0;
  }

  /** Icon mapping for group keys */
  groupIcon(group: ReachabilityGroup): string {
    const map: Record<string, string> = {
      email: 'lucideMail',
      sms_fax: 'lucideMessageSquare',
      direct: 'lucidePhoneForwarded',
      busy: 'lucideBell',
      external: 'lucideExternalLink',
    };
    return map[group.key] ?? 'lucideCircle';
  }
}
